import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { X, Repeat, TrendingUp, TrendingDown, Calendar, Check, Sparkles, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../config/api';
import { 
  FREQUENCIES, 
  CUSTOM_FREQUENCY_UNITS, 
  getFrequencyByValue,
  calculateCustomMultiplier,
  requiresDayOfMonth 
} from '../constants/frequencies';

const BACKEND_URL = API_URL;
const API = `${BACKEND_URL}/api`;

const RecurringOperationForm = ({ open, onClose, calendarId, currency = '€', currentBalance }) => {
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    type: 'debit',
    amount: '',
    category: '',
    day_of_month: 1,
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    has_end_date: false,
    has_occurrence_limit: false,
    total_occurrences: 10,
    frequency_type: 'mensuel',
    frequency_value: 1,
    frequency_unit: 'mois',
    comment: ''
  });

  useEffect(() => {
    if (open && calendarId) {
      loadCategories();
    }
  }, [open, calendarId]);

  const loadCategories = async () => {
    try {
      const response = await axios.get(
        `${API}/categories?calendar_id=${calendarId}`,
        { withCredentials: true }
      );
      setCategories(response.data);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  // Calculs de projection dynamiques avec les nouvelles fréquences
  const projections = useMemo(() => {
    const amount = parseFloat(formData.amount) || 0;
    let perMonth = 0;
    let perYear = 0;
    let occurrencesPerMonth = 0;
    
    const freq = getFrequencyByValue(formData.frequency_type);
    
    if (formData.frequency_type === 'unique') {
      // Ponctuel : pas de récurrence
      occurrencesPerMonth = 0;
      perMonth = 0;
      perYear = amount; // Une seule fois
    } else if (formData.frequency_type === 'personnalise') {
      // Personnalisé : calcul dynamique
      const value = parseInt(formData.frequency_value) || 1;
      const multiplier = calculateCustomMultiplier(formData.frequency_unit, value);
      occurrencesPerMonth = multiplier;
      perMonth = amount * multiplier;
      
      // Calcul annuel selon l'unité
      if (formData.frequency_unit === 'jours') {
        perYear = amount * (365 / value);
      } else if (formData.frequency_unit === 'semaines') {
        perYear = amount * (52 / value);
      } else if (formData.frequency_unit === 'mois') {
        perYear = amount * (12 / value);
      } else if (formData.frequency_unit === 'annees') {
        perYear = amount / value;
      }
    } else if (freq) {
      // Fréquence standard
      occurrencesPerMonth = freq.multiplierToMonthly;
      perMonth = amount * freq.multiplierToMonthly;
      perYear = perMonth * 12;
    }
    
    // Si limite d'occurrences
    let totalAmount = perYear;
    if (formData.has_occurrence_limit && formData.total_occurrences) {
      totalAmount = amount * parseInt(formData.total_occurrences);
    }
    
    return {
      perMonth: perMonth.toFixed(0),
      perYear: perYear.toFixed(0),
      totalAmount: totalAmount.toFixed(0),
      occurrencesPerMonth: occurrencesPerMonth.toFixed(1)
    };
  }, [formData]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFrequencyChange = (frequencyValue) => {
    const selected = getFrequencyByValue(frequencyValue);
    if (selected) {
      const newData = {
        ...formData,
        frequency_type: frequencyValue,
      };
      
      // Définir les valeurs par défaut selon la fréquence
      if (frequencyValue === 'personnalise') {
        newData.frequency_unit = 'jours';
        newData.frequency_value = 1;
      } else if (selected.unit && selected.unitValue) {
        newData.frequency_unit = selected.unit;
        newData.frequency_value = selected.unitValue;
      }
      
      setFormData(newData);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.amount || !formData.category) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setLoading(true);
    try {
      const data = {
        name: formData.name,
        type: formData.type,
        amount: parseFloat(formData.amount),
        category: formData.category,
        day_of_month: parseInt(formData.day_of_month),
        start_date: formData.start_date,
        end_date: formData.has_end_date ? formData.end_date : null,
        frequency_type: formData.frequency_type,
        frequency_value: parseInt(formData.frequency_value) || 1,
        frequency_unit: formData.frequency_unit,
        total_occurrences: formData.has_occurrence_limit ? parseInt(formData.total_occurrences) : null,
        comment: formData.comment || null,
        active: true
      };

      await axios.post(
        `${API}/recurring-operations?calendar_id=${calendarId}`,
        data,
        { withCredentials: true }
      );

      toast.success('Opération récurrente créée ! 🔄');
      onClose();
    } catch (error) {
      console.error('Error creating recurring operation:', error);
      toast.error('Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  const dayOptions = Array.from({ length: 31 }, (_, i) => i + 1);
  const filteredCategories = categories.filter(c => 
    formData.type === 'credit' ? c.type === 'credit' : c.type !== 'credit'
  );
  
  // Déterminer si on doit afficher le jour du mois
  const showDayOfMonth = requiresDayOfMonth(formData.frequency_type, formData.frequency_unit);
  
  // Séparer les fréquences courantes et avancées pour l'affichage
  const commonFrequencies = FREQUENCIES.filter(f => 
    ['hebdomadaire', 'quinzaine', 'mensuel'].includes(f.value)
  );
  const advancedFrequencies = FREQUENCIES.filter(f => 
    !['hebdomadaire', 'quinzaine', 'mensuel'].includes(f.value)
  );

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
      >
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        />
        
        {/* Content */}
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 25 }}
          className="relative w-full sm:max-w-md max-h-[90vh] bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-4 sticky top-0 z-10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                  <Repeat className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-lg font-bold">Nouvelle récurrence</h2>
                  <p className="text-xs text-white/70">Se répète automatiquement</p>
                </div>
              </div>
              <button 
                onClick={onClose}
                className="p-2 rounded-full hover:bg-white/10 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-4 space-y-4 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 180px)' }}>
            {/* Type selector */}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => handleChange('type', 'credit')}
                className={`flex-1 p-3 rounded-xl border-2 transition-all flex items-center justify-center gap-2 ${
                  formData.type === 'credit' 
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-700' 
                    : 'border-gray-200 text-gray-500'
                }`}
              >
                <TrendingUp className="h-5 w-5" />
                <span className="font-medium">Entrée</span>
              </button>
              <button
                type="button"
                onClick={() => handleChange('type', 'debit')}
                className={`flex-1 p-3 rounded-xl border-2 transition-all flex items-center justify-center gap-2 ${
                  formData.type === 'debit' 
                    ? 'border-rose-500 bg-rose-50 text-rose-700' 
                    : 'border-gray-200 text-gray-500'
                }`}
              >
                <TrendingDown className="h-5 w-5" />
                <span className="font-medium">Sortie</span>
              </button>
            </div>

            {/* Name */}
            <div>
              <Label htmlFor="name">Nom de l'opération *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Ex: Loyer, Salaire, Netflix..."
                className="mt-1"
              />
            </div>

            {/* Amount */}
            <div>
              <Label htmlFor="amount">Montant ({currency}) *</Label>
              <Input
                id="amount"
                type="number"
                min="0.01"
                step="0.01"
                value={formData.amount}
                onChange={(e) => handleChange('amount', e.target.value)}
                placeholder="0.00"
                className="mt-1 text-lg font-semibold"
              />
            </div>

            {/* Category */}
            <div>
              <Label htmlFor="category">Catégorie *</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => handleChange('category', value)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Choisir une catégorie" />
                </SelectTrigger>
                <SelectContent>
                  {filteredCategories.length > 0 ? (
                    filteredCategories.map(cat => (
                      <SelectItem key={cat.id} value={cat.name}>
                        {cat.name}
                      </SelectItem>
                    ))
                  ) : (
                    <>
                      <SelectItem value="🏠 Loyer">🏠 Loyer</SelectItem>
                      <SelectItem value="💵 Salaire">💵 Salaire</SelectItem>
                      <SelectItem value="📺 Abonnement">📺 Abonnement</SelectItem>
                      <SelectItem value="🚗 Transport">🚗 Transport</SelectItem>
                      <SelectItem value="💼 Assurance">💼 Assurance</SelectItem>
                      <SelectItem value="📦 Divers">📦 Divers</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Frequency - HARMONISÉ */}
            <div className="bg-purple-50 rounded-xl p-4 space-y-3">
              <Label className="text-purple-800 font-semibold">Fréquence</Label>
              
              {/* Fréquences courantes */}
              <div className="grid grid-cols-3 gap-2">
                {commonFrequencies.map(freq => (
                  <button
                    key={freq.value}
                    type="button"
                    onClick={() => handleFrequencyChange(freq.value)}
                    className={`p-2 rounded-lg text-sm font-medium transition-all ${
                      formData.frequency_type === freq.value
                        ? 'bg-purple-600 text-white'
                        : 'bg-white text-gray-700 border border-purple-200 hover:border-purple-400'
                    }`}
                  >
                    {freq.shortLabel}
                  </button>
                ))}
              </div>
              
              {/* Fréquences avancées (dropdown) */}
              <div className="pt-2 border-t border-purple-200">
                <Label className="text-xs text-purple-600 mb-2 block">Autres fréquences</Label>
                <Select
                  value={advancedFrequencies.some(f => f.value === formData.frequency_type) ? formData.frequency_type : ''}
                  onValueChange={handleFrequencyChange}
                >
                  <SelectTrigger className={`w-full ${
                    advancedFrequencies.some(f => f.value === formData.frequency_type)
                      ? 'bg-purple-600 text-white border-purple-600'
                      : 'bg-white'
                  }`}>
                    <SelectValue placeholder="Choisir une autre fréquence..." />
                  </SelectTrigger>
                  <SelectContent>
                    {advancedFrequencies.map(freq => (
                      <SelectItem key={freq.value} value={freq.value}>
                        <div className="flex flex-col">
                          <span className="font-medium">{freq.label}</span>
                          <span className="text-xs text-gray-500">{freq.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Options personnalisées */}
              {formData.frequency_type === 'personnalise' && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="bg-white rounded-lg p-3 mt-2 border border-purple-200"
                >
                  <Label className="text-sm text-gray-600 mb-2 block">Définir la fréquence</Label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Tous les</span>
                    <Input
                      type="number"
                      min="1"
                      max="365"
                      value={formData.frequency_value}
                      onChange={(e) => handleChange('frequency_value', e.target.value)}
                      className="w-16 text-center"
                      data-testid="custom-frequency-value"
                    />
                    <Select
                      value={formData.frequency_unit}
                      onValueChange={(value) => handleChange('frequency_unit', value)}
                    >
                      <SelectTrigger className="w-32" data-testid="custom-frequency-unit">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CUSTOM_FREQUENCY_UNITS.map(unit => (
                          <SelectItem key={unit.value} value={unit.value}>
                            {unit.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </motion.div>
              )}
              
              {/* Info fréquence unique */}
              {formData.frequency_type === 'unique' && (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="bg-amber-50 rounded-lg p-3 border border-amber-200"
                >
                  <p className="text-sm text-amber-800">
                    <AlertCircle className="inline h-4 w-4 mr-1" />
                    Cette opération n'apparaîtra qu'une seule fois, à la date de début.
                  </p>
                </motion.div>
              )}
            </div>

            {/* Day of month - affiché uniquement si nécessaire */}
            {showDayOfMonth && formData.frequency_type !== 'unique' && (
              <div>
                <Label htmlFor="day">Jour du mois</Label>
                <Select
                  value={formData.day_of_month.toString()}
                  onValueChange={(value) => handleChange('day_of_month', parseInt(value))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Choisir le jour" />
                  </SelectTrigger>
                  <SelectContent>
                    {dayOptions.map(day => (
                      <SelectItem key={day} value={day.toString()}>
                        Le {day} {day > 28 && <span className="text-xs text-gray-400">(ajusté si mois plus court)</span>}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {parseInt(formData.day_of_month) > 28 && (
                  <p className="text-xs text-amber-600 mt-1">
                    Pour les mois plus courts, l'opération sera décalée au dernier jour du mois.
                  </p>
                )}
              </div>
            )}

            {/* Start date */}
            <div>
              <Label htmlFor="start_date">Date de début</Label>
              <Input
                id="start_date"
                type="date"
                value={formData.start_date}
                onChange={(e) => handleChange('start_date', e.target.value)}
                className="mt-1"
              />
            </div>

            {/* Occurrence limit */}
            <div className="bg-amber-50 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="has_occurrence_limit" className="cursor-pointer text-amber-800 font-medium">
                  Limiter le nombre d'occurrences
                </Label>
                <Switch
                  id="has_occurrence_limit"
                  checked={formData.has_occurrence_limit}
                  onCheckedChange={(checked) => handleChange('has_occurrence_limit', checked)}
                />
              </div>
              {formData.has_occurrence_limit && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="space-y-2"
                >
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="1"
                      max="120"
                      value={formData.total_occurrences}
                      onChange={(e) => handleChange('total_occurrences', e.target.value)}
                      className="w-20 text-center"
                    />
                    <span className="text-sm text-amber-700">occurrences au total</span>
                  </div>
                  <p className="text-xs text-amber-600">
                    📊 Suivi automatique : 1/{formData.total_occurrences}, 2/{formData.total_occurrences}...
                  </p>
                </motion.div>
              )}
            </div>

            {/* End date toggle */}
            <div className="bg-gray-50 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="has_end" className="cursor-pointer">Date de fin</Label>
                <Switch
                  id="has_end"
                  checked={formData.has_end_date}
                  onCheckedChange={(checked) => handleChange('has_end_date', checked)}
                />
              </div>
              {formData.has_end_date && (
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => handleChange('end_date', e.target.value)}
                  min={formData.start_date}
                />
              )}
            </div>

            {/* Projections */}
            {parseFloat(formData.amount) > 0 && (
              <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-4"
              >
                <div className="flex items-start gap-3">
                  <Sparkles className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <div className="space-y-2 flex-1">
                    <p className="text-sm text-purple-800 font-medium">Projection</p>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="bg-white/70 rounded-lg p-2 text-center">
                        <div className="text-lg font-bold text-purple-700">{projections.perMonth}€</div>
                        <div className="text-xs text-gray-500">par mois</div>
                      </div>
                      <div className="bg-white/70 rounded-lg p-2 text-center">
                        <div className="text-lg font-bold text-purple-700">{projections.perYear}€</div>
                        <div className="text-xs text-gray-500">par an</div>
                      </div>
                    </div>
                    {formData.has_occurrence_limit && (
                      <div className="bg-white/70 rounded-lg p-2 text-center">
                        <div className="text-lg font-bold text-amber-600">{projections.totalAmount}€</div>
                        <div className="text-xs text-gray-500">total sur {formData.total_occurrences} occurrences</div>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Comment */}
            <div>
              <Label htmlFor="comment">Note (optionnel)</Label>
              <Input
                id="comment"
                value={formData.comment}
                onChange={(e) => handleChange('comment', e.target.value)}
                placeholder="Ajouter une note..."
                className="mt-1"
              />
            </div>

            {/* Calendar sync note */}
            <div className="flex items-start gap-3 bg-blue-50 rounded-xl p-4">
              <Calendar className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="text-blue-800 font-medium">Synchronisation calendrier</p>
                <p className="text-blue-600 text-xs mt-1">
                  Les occurrences apparaîtront automatiquement dans ton calendrier. 
                  Les paiements passés impactent le solde, les futurs sont projetés.
                </p>
              </div>
            </div>
          </form>

          {/* Footer */}
          <div className="p-4 border-t bg-gray-50">
            <div className="flex gap-3">
              <Button variant="outline" onClick={onClose} className="flex-1">
                Annuler
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
              >
                {loading ? (
                  'Création...'
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Créer
                  </>
                )}
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default RecurringOperationForm;
